import { useEffect, useRef, useState } from "react";
import { motion, useInView, animate } from "framer-motion";
import { TrendingUp, TrendingDown, Users, GraduationCap } from "lucide-react";

function Counter({ to, prefix = "", suffix = "", decimals = 0 }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.5 });
  const [val, setVal] = useState(0);

  useEffect(() => {
    if (!inView) return;
    const controls = animate(0, to, {
      duration: 1.8,
      ease: [0.22, 1, 0.36, 1],
      onUpdate: (v) => setVal(v),
    });
    return () => controls.stop();
  }, [inView, to]);

  return (
    <span ref={ref}>
      {prefix}
      {val.toFixed(decimals)}
      {suffix}
    </span>
  );
}

const STATS = [
  { icon: TrendingUp, to: 85, prefix: "+", suffix: "%", label: "Projected Economic Growth", color: "text-emerald-green" },
  { icon: TrendingDown, to: 65, prefix: "-", suffix: "%", label: "Reduction in Migration", color: "text-vibrant-orange" },
  { icon: GraduationCap, to: 12, prefix: "", suffix: "K+", label: "Learners in Pilot Cohorts", color: "text-emerald-green" },
  { icon: Users, to: 100, prefix: "", suffix: "%", label: "Voice-First Coverage", color: "text-vibrant-orange" },
];

export default function Impact() {
  return (
    <section id="impact" data-testid="impact-section" className="bg-light-gray pb-24 md:pb-32">
      <div className="mx-auto max-w-7xl px-6 md:px-12">
        <div className="overflow-hidden rounded-[2.5rem] bg-deep-trust-blue px-8 py-16 md:px-16 md:py-20">
          <div className="mb-14 max-w-2xl">
            <span className="text-sm font-semibold uppercase tracking-[0.2em] text-emerald-green">
              Measured Impact
            </span>
            <h2 className="mt-3 font-heading text-4xl font-extrabold tracking-tight text-white sm:text-5xl">
              Numbers that change destinies.
            </h2>
          </div>

          <div className="grid grid-cols-2 gap-8 md:grid-cols-4 md:gap-6">
            {STATS.map((s, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.4 }}
                transition={{ duration: 0.6, delay: i * 0.1 }}
                data-testid={`impact-stat-${i}`}
              >
                <s.icon className={`${s.color} mb-4`} size={28} />
                <div className="font-heading text-4xl font-extrabold tracking-tight text-white sm:text-5xl">
                  <Counter to={s.to} prefix={s.prefix} suffix={s.suffix} />
                </div>
                <p className="mt-2 text-sm leading-snug text-white/60">{s.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
